"use client";

// Fachadas: mostram uma capa leve e só criam o iframe (pesado: ~500 KB de JS
// do YouTube, ~1 MB do Spotify) quando a pessoa clica. Mantém o LCP baixo.

import Image from "next/image";
import { useEffect, useState } from "react";
import { pausePlayer } from "./MiniPlayer";
import { Play, Spotify } from "./Icons";

/** Liga o iframe e cuida da convivência com o player do hit (um som por vez). */
function useExclusive(): [boolean, () => void] {
  const [on, setOn] = useState(false);
  useEffect(() => {
    if (!on) return;
    const off = () => setOn(false);
    window.addEventListener("mf:pause-others", off);
    return () => window.removeEventListener("mf:pause-others", off);
  }, [on]);
  return [
    on,
    () => {
      pausePlayer();
      setOn(true);
    },
  ];
}

export function VideoFacade({
  id,
  title,
  thumb,
  vertical = false,
  featured = false,
  priority = false,
}: {
  id: string;
  title: string;
  thumb: string;
  vertical?: boolean;
  featured?: boolean;
  priority?: boolean;
}) {
  const [on, start] = useExclusive();
  const ratio = vertical ? "aspect-[9/16]" : "aspect-video";

  if (on) {
    return (
      <iframe
        className={`${ratio} w-full rounded-[inherit] bg-black`}
        src={`https://www.youtube-nocookie.com/embed/${id}?autoplay=1&rel=0&playsinline=1`}
        title={title}
        allow="autoplay; encrypted-media; picture-in-picture; fullscreen"
        allowFullScreen
      />
    );
  }

  return (
    <button
      type="button"
      onClick={start}
      data-track="play"
      data-label={title.slice(0, 100)}
      className={`group relative ${ratio} w-full overflow-hidden rounded-[inherit] bg-noite text-left`}
      aria-label={`Assistir: ${title}`}
    >
      <Image
        src={vertical ? `https://i.ytimg.com/vi/${id}/oar2.jpg` : thumb}
        alt=""
        fill
        preload={priority}
        sizes={vertical ? "(max-width: 768px) 60vw, 240px" : "(max-width: 768px) 100vw, 60vw"}
        className="object-cover transition duration-700 group-hover:scale-105 group-hover:saturate-125"
      />
      <span className="absolute inset-0 bg-gradient-to-t from-noite via-noite/10 to-transparent" />
      <span className="absolute left-1/2 top-1/2 grid size-16 -translate-x-1/2 -translate-y-1/2 place-items-center rounded-full border border-champanhe/50 bg-noite/40 text-2xl text-champanhe backdrop-blur-md transition group-hover:scale-110 group-hover:bg-rubi group-hover:border-rubi">
        <Play className="translate-x-0.5" />
      </span>
      <span className={`absolute inset-x-0 bottom-0 p-4 font-sans text-sm leading-snug text-areia ${vertical ? "line-clamp-3" : featured ? "line-clamp-2 md:p-6 md:text-lg" : "line-clamp-2 text-[0.8rem]"}`}>
        {title}
      </span>
    </button>
  );
}

export function SpotifyFacade({
  playlistId,
  image,
  title = "This Is Mariana Fagundes",
}: {
  playlistId: string;
  image: string | null;
  title?: string;
}) {
  const [on, start] = useExclusive();

  if (on) {
    return (
      <iframe
        className="h-[352px] w-full rounded-2xl"
        src={`https://open.spotify.com/embed/playlist/${playlistId}?utm_source=generator&theme=0`}
        title={title}
        allow="autoplay; clipboard-write; encrypted-media; fullscreen; picture-in-picture"
        loading="lazy"
      />
    );
  }

  return (
    <button
      type="button"
      onClick={start}
      data-track="play"
      data-label="Spotify playlist"
      className="group relative isolate flex h-[352px] w-full items-end overflow-hidden rounded-2xl border border-champanhe/15 bg-vinho/60 p-6 text-left transition hover:border-[#1ed760]/60"
    >
      {image && (
        <Image
          src={image}
          alt=""
          fill
          quality={50}
          sizes="(max-width: 768px) 100vw, 50vw"
          className="-z-10 object-cover object-[50%_30%] opacity-60 transition duration-700 group-hover:scale-105 group-hover:opacity-75"
        />
      )}
      <span className="absolute inset-0 -z-10 bg-gradient-to-t from-noite via-noite/50 to-transparent" />
      <span className="flex w-full items-center gap-5">
        <span className="min-w-0 flex-1">
          <span className="block text-xs uppercase tracking-[0.3em] text-champanhe/60">Playlist oficial · Spotify</span>
          <span className="mt-1 block font-display text-3xl uppercase text-areia">{title}</span>
        </span>
        <span className="grid size-14 place-items-center rounded-full bg-[#1ed760] text-2xl text-black transition group-hover:scale-110">
          <Spotify />
        </span>
      </span>
    </button>
  );
}
